/* La Guía del viaje (案内): lo que no es de un día concreto. Antes de salir, compras, la guía práctica
   por situaciones, exámenes, la noche, frases, los hoteles para el taxi y el cambio. */
import { useState } from 'react';
import { cambioDe, casillaHecha, ponerCambio, useApuntes } from '../datos/apuntes.ts';
import { comprasEnCualquierSitio, esCabecera, tituloCompra } from '../datos/compras.ts';
import type { Compra, Lugar, Viaje } from '../datos/tipos.ts';
import { Casilla, Documento, progresoCasillas } from '../componentes/Documento.tsx';
import { Hoja } from '../componentes/Hoja.tsx';
import { euros, fechaCorta, miles, num2 } from '../texto.ts';
import { Buscador } from './Buscar.tsx';
import { IdeasDeCompra, MiLista } from '../componentes/Ideas.tsx';
import { cambiarClave, salir, useSesion } from '../datos/sesion.ts';
import { FrasesGuia } from '../componentes/Frases.tsx';
import { ListaExamenes } from './Examenes.tsx';
import { TarjetaHotel } from './Hotel.tsx';

export type PestanaGuia = 'antes' | 'compras' | 'guia' | 'examenes' | 'noche' | 'frases' | 'hoteles' | 'cambio' | 'cuenta';
const PESTANAS: [PestanaGuia, string, string][] = [
  ['antes', '準備', 'Antes de salir'], ['compras', '買物', 'Compras'], ['guia', '案内', 'Guía práctica'], ['examenes', '試験', 'Exámenes'],
  ['noche', '夜', 'Noche'], ['frases', '言葉', 'Frases'], ['hoteles', '宿', 'Hoteles'], ['cambio', '円', 'Cambio'], ['cuenta', '私', 'Cuenta'],
];

/* ───────── Compras ───────── */
function Compras({ v }: { v: Viaje }) {
  const { yo } = useSesion();
  // David trae su lista del documento; Yago y Daniel, solo lo común: ellos empiezan por su lista
  const [vista, setVista] = useState<'documento' | 'mia' | 'ideas'>(yo?.rol === 'propietario' ? 'documento' : 'mia');
  return (
    <>
      <div className="filtros" role="group" aria-label="Qué ver">
        <button type="button" aria-pressed={vista === 'mia'} onClick={() => setVista('mia')}>Mi lista</button>
        <button type="button" aria-pressed={vista === 'ideas'} onClick={() => setVista('ideas')}>Ideas</button>
        <button type="button" aria-pressed={vista === 'documento'} onClick={() => setVista('documento')}>{yo?.rol === 'propietario' ? 'Mis documentos' : 'Comunes'}</button>
      </div>
      {vista === 'mia' && <MiLista />}
      {vista === 'ideas' && <IdeasDeCompra />}
      {vista === 'documento' && <ComprasDelDocumento v={v} />}
    </>
  );
}

function ComprasDelDocumento({ v }: { v: Viaje }) {
  useApuntes();
  const [lista, setLista] = useState<Compra['lista']>(v.compras.some(c => c.lista === 'regalos') ? 'regalos' : 'comunes');
  const [ocultar, setOcultar] = useState(false);
  const todas = v.compras.filter(c => !(c.lista === 'mias' && esCabecera(c) && v.compras.some(x => x.lista === 'mias' && !esCabecera(x) && x.categoria === c.categoria)));
  const hechas = todas.filter(c => casillaHecha(c.id)).length;
  const de = (l: Compra['lista']) => todas.filter(c => c.lista === l);
  const meta = (c: Compra) => [c.donde && c.donde !== '—' ? c.donde : '', c.presupuesto ? `${euros(c.presupuesto)}` : '', c.prioridad ? `prioridad ${c.prioridad}` : '', c.cantidad && c.cantidad !== '—' ? c.cantidad : ''].filter(Boolean).join(' · ');
  const visibles = de(lista).filter(c => !ocultar || !casillaHecha(c.id));
  // «Mías» va por categorías, con su presupuesto
  const grupos = lista === 'mias'
    ? [...new Set(visibles.map(c => c.categoria ?? ''))].map(cat => ({ cat, presupuesto: v.compras.find(x => x.lista === 'mias' && esCabecera(x) && x.categoria === cat)?.presupuesto, items: visibles.filter(c => (c.categoria ?? '') === cat) }))
    : [{ cat: '', presupuesto: undefined, items: visibles }];
  return (
    <>
      <p className="texto"><b>{hechas}</b> de {todas.length} compradas · las de cada ciudad salen también en la hoja de su día.</p>
      <div className="filtros" role="group" aria-label="Qué lista">
        {([['regalos', 'Regalos'], ['mias', 'Mías'], ['comunes', 'Comunes']] as [Compra['lista'], string][]).filter(([k]) => de(k).length > 0).map(([k, t]) => (
          <button key={k} type="button" aria-pressed={k === lista} onClick={() => setLista(k)}>{t} · {de(k).filter(c => !casillaHecha(c.id)).length}</button>
        ))}
        <button type="button" aria-pressed={ocultar} onClick={() => setOcultar(x => !x)}>Ocultar compradas</button>
      </div>
      {grupos.map(g => (
        <section key={g.cat} className="doc-seccion">
          {g.cat && <h3>{g.cat}{g.presupuesto ? ` · ${euros(g.presupuesto)}` : ''}</h3>}
          {g.items.map(c => (
            <Casilla key={c.id} id={c.id}>
              <b>{tituloCompra(c)}</b>
              {meta(c) && <small className="casilla__meta">{meta(c)}</small>}
            </Casilla>
          ))}
        </section>
      ))}
      {lista !== 'mias' && <p className="texto" style={{ fontSize: '.85rem' }}>En cualquier Donki o súper sirven {comprasEnCualquierSitio(v.compras).length} de estas.</p>}
    </>
  );
}

/* ───────── Guía práctica, por situaciones ───────── */
const KANJI_SITUACION: [RegExp, string][] = [
  [/apps|cuentas/i, '携'], [/haneda|vuelo|conexión/i, '空'], [/equipaje|yamato/i, '荷'], [/alojamiento/i, '宿'],
  [/tren|transporte/i, '駅'], [/aparcamiento/i, '駐'], [/conducci|jdm/i, '車'], [/dinero|tax/i, '円'], [/gastronom/i, '食'],
  [/konbini/i, '店'], [/compras|regalos/i, '買'], [/riesgo|plan b/i, '備'], [/fuentes/i, '報'], [/ubicaciones/i, '所'],
];
function GuiaPractica({ v }: { v: Viaje }) {
  const doc = v.documentos.consideraciones;
  const [abierta, setAbierta] = useState<string | null>(null);
  const secciones = doc.secciones.filter(s => s.titulo);
  const s = secciones.find(x => x.id === abierta);
  if (s) {
    return (
      <>
        <button type="button" className="volver" onClick={() => setAbierta(null)}>‹ Todas las situaciones</button>
        <Documento doc={doc} secciones={[s.id]} />
      </>
    );
  }
  return (
    <div className="situaciones">
      {secciones.map(x => (
        <button key={x.id} type="button" className="situacion" onClick={() => setAbierta(x.id)}>
          <span lang="ja" aria-hidden="true">{KANJI_SITUACION.find(([re]) => re.test(x.titulo))?.[1] ?? '案'}</span>
          {x.titulo}
        </button>
      ))}
    </div>
  );
}

/* ───────── Frases: se tocan y salen en grande para enseñarlas ───────── */
/* ───────── Cambio ¥ ↔ € (26e) ───────── */
function Cambio({ v }: { v: Viaje }) {
  useApuntes();
  const cambio = cambioDe(v.viaje.yenPorEuro);
  const [yen, setYen] = useState('1000');
  const eur = (+yen || 0) / cambio;
  return (
    <>
      <label className="conversor">
        <span>Yenes</span>
        <input type="number" inputMode="numeric" value={yen} onChange={e => setYen(e.target.value)} />
      </label>
      <label className="conversor">
        <span>Euros</span>
        <input type="number" inputMode="decimal" value={eur ? eur.toFixed(2) : ''} onChange={e => setYen(String(Math.round((+e.target.value || 0) * cambio)))} />
      </label>
      <label className="conversor">
        <span>1 € son</span>
        <input type="number" inputMode="decimal" min="50" max="400" step="0.1" defaultValue={cambio} key={cambio}
          onBlur={e => { const x = +e.target.value; if (x >= 50 && x <= 400) ponerCambio(x === v.viaje.yenPorEuro ? null : x); }} />
        <span>¥</span>
      </label>
      <p className="texto" style={{ fontSize: '.85rem' }}>
        El cambio que pongas vale para los gastos y el presupuesto. El de la hoja de gastos es {miles(v.viaje.yenPorEuro)} ¥.
        {cambio !== v.viaje.yenPorEuro && <> <button type="button" className="enlace-texto" onClick={() => ponerCambio(null)}>Volver a ese</button></>}
      </p>
    </>
  );
}

/* ───────── Cuenta: quién eres, el grupo, la contraseña y salir ───────── */
function Cuenta() {
  const { yo, grupo } = useSesion();
  const { pendientes } = useApuntes();
  const [actual, setActual] = useState(''), [nueva, setNueva] = useState('');
  const [mensaje, setMensaje] = useState<{ bien: boolean; texto: string } | null>(null);
  const cambiar = async () => {
    const r = await cambiarClave(actual, nueva);
    setMensaje(r ? { bien: false, texto: r } : { bien: true, texto: 'Contraseña cambiada. Las sesiones de otros móviles se han cerrado.' });
    if (!r) { setActual(''); setNueva(''); }
  };
  return (
    <>
      <p className="texto">Has entrado como <b>{yo?.nombre ?? '—'}</b>{yo?.rol === 'propietario' ? ' (administras las cuentas)' : ''}.</p>
      <p className="texto">En el grupo: {grupo.map(g => g.nombre).join(', ') || '—'}. El itinerario, las paradas hechas, las notas y las frases son de los tres; las casillas de Preparativos y los gastos, de cada uno.</p>
      <h3>Cambiar la contraseña</h3>
      <div className="acceso__form acceso__form--claro">
        <label>La de ahora<input type="password" autoComplete="current-password" value={actual} onChange={e => setActual(e.target.value)} /></label>
        <label>La nueva (mínimo 10 caracteres)<input type="password" autoComplete="new-password" minLength={10} value={nueva} onChange={e => setNueva(e.target.value)} /></label>
        <button type="button" className="accion" disabled={!actual || nueva.length < 10} onClick={() => void cambiar()}>Cambiar</button>
        {mensaje && <p className={mensaje.bien ? 'texto' : 'aviso'} role="status">{mensaje.texto}</p>}
      </div>
      <h3>Salir</h3>
      <p className="texto">{pendientes.length ? `Hay ${pendientes.length} cambios sin subir: si sales ahora se pierden. Espera a tener red.` : 'Al salir se borra de este móvil lo tuyo (gastos, casillas…). Vuelve a bajarse al entrar.'}</p>
      <button type="button" className="accion" onClick={() => { if (!pendientes.length || confirm('Hay cambios sin subir que se perderán. ¿Salir igualmente?')) void salir(); }}>Cerrar sesión</button>
    </>
  );
}

/* ───────── La hoja ───────── */
/** El progreso de Preparativos: es lo único de «Antes de salir» que cambia al marcar una casilla. */
function ProgresoAntes({ v }: { v: Viaje }) {
  useApuntes();
  const preparar = progresoCasillas([v.documentos.preparativos, v.documentos.examenes]);
  return (
    <>
      <div className="progreso" role="img" aria-label={`${preparar.hechas} de ${preparar.total} tareas hechas`}>
        <i style={{ width: `${preparar.total ? (preparar.hechas / preparar.total) * 100 : 0}%` }} />
      </div>
      <p className="texto"><b>{preparar.hechas}</b> de {preparar.total} tareas hechas. Después del día 10, cada gestión sale también en la hoja de su día.</p>
    </>
  );
}

export function HojaGuia({ v, inicial = 'antes', onCerrada, onIr }: { v: Viaje; inicial?: PestanaGuia; onCerrada: () => void; onIr: (n: number, bloque: string) => void }) {
  const [p, setP] = useState<PestanaGuia>(inicial);
  const [q, setQ] = useState('');
  const buscando = q.trim().length >= 2;
  const hoteles = [...new Set(v.dias.map(d => d.hotel).filter(Boolean) as string[])]
    .map(id => v.lugares.find(l => l.id === id)).filter((l): l is Lugar => !!l);
  return (
    <Hoja id="hoja-guia" titulo="guia-titulo" onCerrada={onCerrada}>
      {() => (
        <>
          <p className="hoja__kicker">案内 · LA GUÍA DEL VIAJE</p>
          <h2 id="guia-titulo">{buscando ? 'Buscar' : PESTANAS.find(x => x[0] === p)![2]}</h2>
          <input className="buscar" type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar: kart, localizador, Glico, ramen…" aria-label="Buscar en todo el viaje" />
          {buscando && <Buscador v={v} q={q} onIr={onIr} />}
          {!buscando && <>
          <nav className="pestanas" aria-label="Partes de la guía">
            {PESTANAS.filter(([k]) => k !== 'examenes' || v.examenes.length > 0).map(([k, kanji, t]) => (
              <button key={k} type="button" aria-pressed={k === p} onClick={() => setP(k)}><span lang="ja" aria-hidden="true">{kanji}</span>{t}</button>
            ))}
          </nav>
          {p === 'antes' && (
            <>
              <ProgresoAntes v={v} />
              <Documento doc={v.documentos.preparativos} />
              <Documento doc={v.documentos.examenes} secciones={v.documentos.examenes.secciones.filter(s => /antes/i.test(s.titulo)).map(s => s.id)} />
            </>
          )}
          {p === 'compras' && <Compras v={v} />}
          {p === 'guia' && <GuiaPractica v={v} />}
          {p === 'examenes' && (
            <>
              <ListaExamenes v={v} examenes={v.examenes} />
              {(v.examenesQueNoHara ?? []).map(a => <p key={a} className="aviso"><b>{a}: no se hace.</b> Decidido el 6 de octubre (coincide con el teamLab). Lo que diga el documento de abajo sobre este examen ya no vale.</p>)}
              <Documento doc={v.documentos.examenes} secciones={v.documentos.examenes.secciones.filter(s => !/cuatro exámenes/i.test(s.titulo)).map(s => s.id)} />
            </>
          )}
          {p === 'noche' && (
            <>
              <ol className="noches">
                {v.noches.map(x => {
                  const d = v.dias[x.dia - 1], sale = /^S[IÍ]/.test(x.salir) ? 'si' : /^NO/.test(x.salir) ? 'no' : 'quiza';
                  return (
                    <li key={x.dia} data-sale={sale}>
                      <span className="noches__dia">JP{num2(x.dia)}<small>{d ? fechaCorta(d) : ''}</small></span>
                      <span className="noches__que"><span><b>{x.salir.toLowerCase()}</b> · cita {x.cita.toLowerCase()}</span><small>{x.lectura}</small></span>
                    </li>
                  );
                })}
              </ol>
              {/* El resto del documento, sin la tabla de noches (ya está arriba, en corto) */}
              <Documento doc={v.documentos.privado} secciones={v.documentos.privado.secciones
                .filter(sec => !sec.bloques.some(b => b.tipo === 'tabla' && b.filas.length >= 10 && /salir/i.test(b.cabecera.join(' ')))).map(sec => sec.id)} />
            </>
          )}
          {p === 'frases' && <FrasesGuia v={v} />}
          {p === 'hoteles' && hoteles.map(l => <TarjetaHotel key={l.id} v={v} lugar={l} />)}
          {p === 'cambio' && <Cambio v={v} />}
          {p === 'cuenta' && <Cuenta />}
          </>}
        </>
      )}
    </Hoja>
  );
}
